/**
 * Attachment drop zone for the feedback dialog.
 *
 * Accepts files via drag & drop or a file picker, validates them against the
 * server-provided limits and lists the accepted files as removable chips.
 */

import { useCallback, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import type { FeedbackConfig } from '../../types/feedback';

interface FeedbackDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  config: FeedbackConfig;
  disabled?: boolean;
}

/** Human-readable byte size, e.g. `5 MB`. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function extensionOf(name: string): string {
  const index = name.lastIndexOf('.');
  return index >= 0 ? name.slice(index + 1).toLowerCase() : '';
}

export function FeedbackDropzone({ files, onChange, config, disabled }: FeedbackDropzoneProps) {
  const { t } = useTranslation('feedback');
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;

      const accepted = [...files];
      let rejection: string | null = null;

      for (const file of incoming) {
        if (accepted.length >= config.max_files) {
          rejection = t('errors.tooManyFiles', { count: config.max_files });
          break;
        }
        if (accepted.some((f) => f.name === file.name && f.size === file.size)) {
          rejection = t('errors.duplicateFile', { name: file.name });
          continue;
        }
        if (file.size > config.max_file_bytes) {
          rejection = t('errors.fileTooLarge', {
            name: file.name,
            size: formatBytes(config.max_file_bytes),
          });
          continue;
        }
        if (!config.allowed_extensions.includes(extensionOf(file.name))) {
          rejection = t('errors.fileTypeNotAllowed', { name: file.name });
          continue;
        }
        accepted.push(file);
      }

      setError(rejection);
      if (accepted.length !== files.length) onChange(accepted);
    },
    [config, files, onChange, t]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      if (disabled) return;
      addFiles(Array.from(event.dataTransfer.files));
    },
    [addFiles, disabled]
  );

  const handleSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      addFiles(Array.from(event.target.files ?? []));
      // Reset so selecting the same file again re-triggers `change`.
      event.target.value = '';
    },
    [addFiles]
  );

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const isFull = files.length >= config.max_files;

  return (
    <Box>
      <Box
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t('dropzone.ariaLabel')}
        aria-describedby={`${inputId}-hint`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          py: 2.5,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          bgcolor: isDragOver ? 'action.hover' : 'transparent',
          color: 'text.secondary',
          cursor: disabled || isFull ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 120ms, background-color 120ms',
          '&:hover': disabled || isFull ? undefined : { borderColor: 'text.secondary' },
        }}
      >
        <FileUploadOutlinedIcon fontSize="small" />
        <Typography variant="body2" textAlign="center">
          <Trans
            t={t}
            i18nKey="dropzone.prompt"
            components={[
              <Link
                key="browse"
                component="span"
                underline="always"
                sx={{ cursor: 'inherit' }}
              />,
            ]}
          />
        </Typography>
        <Typography id={`${inputId}-hint`} variant="caption" textAlign="center">
          {t('dropzone.hint', {
            count: config.max_files,
            size: formatBytes(config.max_file_bytes),
          })}
        </Typography>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          hidden
          disabled={disabled}
          accept={config.allowed_extensions.map((ext) => `.${ext}`).join(',')}
          onChange={handleSelect}
        />
      </Box>

      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {files.map((file) => (
            <Chip
              key={`${file.name}-${file.size}-${file.lastModified}`}
              size="small"
              variant="outlined"
              label={`${file.name} · ${formatBytes(file.size)}`}
              disabled={disabled}
              onDelete={() => {
                setError(null);
                onChange(files.filter((f) => f !== file));
              }}
              aria-label={t('dropzone.remove', { name: file.name })}
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
